import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'One-Click Audio Note Taking';
const heroDescription =
  'Capture important insights while listening to documents. Automatically timestamped notes sync across all your devices for seamless study sessions.';

const featureTitle = heroTitle;
const featureSubtitle = 'Never miss important insights';
const featureDescription = heroDescription;
const featureBadge = 'Study Smart';
const featureVariant = 'default';
const featureCtaText = 'Start Taking Notes';

const featureFeatures = [
  {
    title: 'Instant Note Capture',
    description:
      'One-click button captures the last 2 sentences you heard, no typing required.',
  },
  {
    title: 'Auto-Timestamped',
    description:
      'Every note includes precise timestamps so you can jump back to that exact moment in the audio.',
  },
  {
    title: 'Cross-Device Sync',
    description:
      'Access your notes on any device - phone, tablet, or computer. Everything stays in sync automatically.',
  },
];

const comparisonTableData = {
  title: "How We're Different from Popular Apps",
  description:
    'Compare our audio note-taking approach with voice memo apps, meeting transcription tools, and traditional note-taking platforms.',
  columns: [
    { header: 'Our Audio Notes', isHighlighted: true },
    { header: 'Voice Memo Apps' },
    { header: 'Meeting Transcription', subheader: 'Otter' },
    { header: 'Traditional Notes', subheader: 'Notion' },
  ],
  rows: [
    {
      feature: 'Content Capture Method',
      description: 'How notes are captured and what content is preserved.',
      values: [
        'Captures exact content sentences',
        'Requires you to speak summaries',
        'Transcribes your speech',
        'Requires typing while listening',
      ],
    },
    {
      feature: 'Workflow Integration',
      description: 'How note-taking integrates with your listening workflow.',
      values: [
        'One-click while listening',
        'Must pause to record',
        'Designed for live conversations',
        'Must stop audio to write',
      ],
    },
    {
      feature: 'Source Connection',
      description: 'How notes connect back to the original audio content.',
      values: [
        'Auto-timestamped to source',
        'No connection to source material',
        'Creates separate transcripts',
        'Manual organization required',
      ],
    },
    {
      feature: 'Content Accuracy',
      description: 'Quality and accuracy of captured information.',
      values: [
        "Preserves author's language",
        'Relies on your interpretation',
        'Good for meetings, poor for content',
        'Great for organization, poor for audio',
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Smart Note-Taking While You Listen to Documents',
    content:
      "Traditional note-taking apps like Notion and Otter require you to speak your thoughts aloud or type while listening, but our audio note-taking tool works differently - it captures key insights directly from the content you're already listening to. While reading research papers, academic articles, or study materials through our text-to-speech technology, simply click one button to instantly save the last two sentences you heard, complete with precise timestamps for easy reference.",
    contentSecondary:
      'This seamless approach eliminates the need to pause audio, switch apps, or interrupt your learning flow. Unlike voice memo apps that require you to dictate summaries, or transcription tools that convert speech to text, our system captures the actual content moments that matter most to you. Perfect for students reviewing literature, researchers processing papers, or professionals absorbing industry reports - you stay focused on listening while building a comprehensive, searchable library of key insights.',
  },
  {
    title: 'Why This Beats Traditional Note Apps for Audio Learning',
    content:
      "While popular note-taking apps like Otter excel at meeting transcription and Notion provides powerful organization, they're not designed for the unique challenge of taking notes while consuming audio content. Voice transcription apps require you to speak your thoughts, which disrupts the listening experience, while traditional note apps force you to type or write, taking your attention away from the audio entirely.",
    contentSecondary:
      'Our audio note-taking solution solves this fundamental problem by letting the content speak for itself. Instead of trying to summarize or paraphrase what you heard (like with voice memos), you capture the exact sentences that resonated with you, maintaining the author\'s precise language and context. Users consistently report better comprehension and more useful notes compared to traditional methods, with testimonials noting "finally found a way to take notes without missing important parts of lectures" and "much more efficient than trying to write while listening."',
  },
];

export const Route = createFileRoute('/_marketing/_seo/audio-note-taking')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Audio Note Taking While Listening | Capture Insights from Documents | Listening',
        description:
          'One-click audio note taking while listening to documents. Capture timestamped insights from research papers, lectures, and study materials instantly',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
